import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Route as RouteIcon, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import FunnelChart, { pct } from "@/components/journeys/FunnelChart";

/** The evidence on a case opened by a journey break: the funnel, and who is stuck. */
export default function JourneyBreakPanel({ journey }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get(`/journeys/${journey.id}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [journey.id]);

  // The evaluation that opened this case may since have been followed by
  // others; the case's own numbers are the ones shown in the headline.
  const latest = detail?.latest;
  const stuck = detail?.stuck?.step_key === journey.step_key ? detail.stuck.people : [];
  const known = stuck.filter((p) => p.customer_id);

  // Diagnosed once, when the break opened this case, so the newest evaluation
  // carrying one is the explanation for the break still being looked at.
  const diagnosis = (detail?.history || []).find((e) => e.diagnosis?.summary)?.diagnosis;

  return (
    <div className="card p-5 mb-4" data-testid="journey-break-panel">
      <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider"
           style={{ color: "var(--text-muted)" }}>
        <RouteIcon size={12} /> Journey break
        <Link to={`/journeys/${journey.id}`} className="ml-auto normal-case tracking-normal underline"
              style={{ color: "var(--text-secondary)" }}>
          {journey.name}
        </Link>
      </div>
      <div className="font-medium mb-1">
        {journey.previous_label} → {journey.step_label}: {pct(journey.conversion)}{" "}
        <span style={{ color: "var(--text-secondary)" }}>(usually {pct(journey.baseline)})</span>
      </div>
      <div className="text-[12.5px] mb-4" style={{ color: "var(--text-secondary)" }}>
        {journey.affected_people} people stuck
        {journey.affected_customers ? `, ${journey.affected_customers} known customers` : ""}
        {journey.affected_arr ? ` worth ${Math.round(journey.affected_arr).toLocaleString()} a year` : ""}.
      </div>

      {latest && detail && (
        <FunnelChart
          steps={detail.steps.map((s) => ({ key: s.key, label: s.label,
            count: latest.step_counts[s.key] || 0, conversion: latest.step_conversion[s.key] }))}
          baseline={latest.baseline_conversion}
          brokenKey={latest.broken_step_key}
        />
      )}

      {diagnosis && (
        <div className="mt-4" data-testid="journey-break-diagnosis">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Sparkles size={13} style={{ color: "var(--risk-waiting)" }} />
            <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
              Why this broke (informs only)
            </div>
            <span className="badge b-muted ml-auto">
              {Math.round((diagnosis.confidence || 0) * 100)}% confidence
            </span>
          </div>
          <div className="text-[13px] leading-snug">{diagnosis.summary}</div>

          {diagnosis.probable_causes?.length > 0 && (
            <ul className="mt-2.5 text-[12px] space-y-1" style={{ color: "var(--text-secondary)" }}>
              {diagnosis.probable_causes.map((c, i) => (
                <li key={i} className="leading-snug">
                  › {c.cause}
                  <span className="ml-1" style={{ color: "var(--text-muted)" }}>
                    ({Math.round((c.likelihood || 0) * 100)}%)
                  </span>
                </li>
              ))}
            </ul>
          )}

          {diagnosis.recommended_checks?.length > 0 && (
            <div className="mt-2.5">
              <div className="text-[10.5px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>
                Check first
              </div>
              <ul className="text-[11.5px] space-y-0.5" style={{ color: "var(--text-secondary)" }}>
                {diagnosis.recommended_checks.map((c, i) => <li key={i}>— {c}</li>)}
              </ul>
            </div>
          )}

          {/* Said plainly, because a diagnosis drawn from nothing reads just
              as fluently as one drawn from forty matching decline messages. */}
          {!diagnosis.generated_by?.described_events && (
            <div className="mt-2.5 text-[11.5px]" style={{ color: "var(--text-muted)" }}>
              None of the stuck customers' events carried a description, so this rests on
              the funnel alone. Set a description property on the connector sending these
              events to give the next break more to go on.
            </div>
          )}
        </div>
      )}

      {known.length > 0 && (
        <div className="mt-4 text-[12px]" data-testid="journey-stuck-customers">
          <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>
            Known customers stuck
          </div>
          <div className="flex flex-wrap gap-1.5">
            {known.slice(0, 30).map((p) => (
              <Link key={p.subject} to={`/customers/${p.customer_id}`} className="badge b-muted">
                {p.customer_name || p.subject}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
